import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Sparkles, Wand2, Search } from 'lucide-react';
import { toast } from 'sonner';

const SUGGESTED_TOPICS = [
  'Dollar cost averaging',
  'Reading a 10-K',
  'Covered calls basics',
  'Dividend reinvestment',
  'Bond ladders',
  'Reading earnings reports',
];

/**
 * Course generator form — topic input, mode toggle (quick / full), and
 * optional module configuration. Submits to onGenerate(payload).
 */
const CourseGenerator = ({ onGenerate, generating, quotaRemaining }) => {
  const [topic, setTopic] = useState('');
  const [mode, setMode] = useState('full');
  const [modules, setModules] = useState(4);
  const [lessonsPerModule, setLessonsPerModule] = useState(3);

  const submit = async (e) => {
    e?.preventDefault();
    const trimmed = topic.trim();
    if (trimmed.length < 3) {
      toast.error('Enter a topic of at least 3 characters');
      return;
    }
    await onGenerate({
      topic: trimmed,
      mode,
      modules,
      lessons_per_module: lessonsPerModule,
    });
  };

  return (
    <Card className="bg-white border-gray-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-gray-900">
          <Wand2 className="w-5 h-5 text-purple-600" />
          Generate a new course
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={submit}>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">
              Topic
            </label>
            <Input
              data-testid="course-topic-input"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g. How to read an earnings report"
              className="mt-1"
              maxLength={200}
            />
            <div className="mt-2 flex flex-wrap gap-2">
              {SUGGESTED_TOPICS.map((t) => (
                <button
                  type="button"
                  key={t}
                  onClick={() => setTopic(t)}
                  className="text-xs px-2.5 py-1 rounded-full border border-gray-200 bg-gray-50 hover:bg-purple-50 hover:border-purple-200 text-gray-700"
                  data-testid={`course-suggested-${t.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-1">
              <label className="text-xs uppercase tracking-wider text-gray-500">Mode</label>
              <div className="mt-1 grid grid-cols-2 rounded-lg border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setMode('quick')}
                  className={`text-sm py-2 flex items-center justify-center gap-1 ${
                    mode === 'quick'
                      ? 'bg-purple-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  data-testid="course-mode-quick"
                >
                  <Search className="w-3.5 h-3.5" /> Quick search
                </button>
                <button
                  type="button"
                  onClick={() => setMode('full')}
                  className={`text-sm py-2 flex items-center justify-center gap-1 ${
                    mode === 'full'
                      ? 'bg-purple-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  data-testid="course-mode-full"
                >
                  <Sparkles className="w-3.5 h-3.5" /> Full course
                </button>
              </div>
            </div>

            {mode === 'full' && (
              <>
                <div>
                  <label className="text-xs uppercase tracking-wider text-gray-500">
                    Modules
                  </label>
                  <Input
                    type="number"
                    min={2}
                    max={6}
                    value={modules}
                    onChange={(e) => setModules(Number(e.target.value) || 4)}
                    className="mt-1"
                    data-testid="course-modules-input"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-gray-500">
                    Lessons / module
                  </label>
                  <Input
                    type="number"
                    min={2}
                    max={5}
                    value={lessonsPerModule}
                    onChange={(e) =>
                      setLessonsPerModule(Number(e.target.value) || 3)
                    }
                    className="mt-1"
                    data-testid="course-lessons-input"
                  />
                </div>
              </>
            )}
          </div>

          <div className="flex items-center justify-between pt-2">
            <p className="text-xs text-gray-500">
              {typeof quotaRemaining === 'number'
                ? `${quotaRemaining} free generation${quotaRemaining === 1 ? '' : 's'} left`
                : 'Unlimited generations (Pro)'}
            </p>
            <Button
              type="submit"
              disabled={generating}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="course-generate-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {generating ? 'Generating…' : 'Generate course'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default CourseGenerator;
